import { isReactive, isRef, Ref, unref, watch } from 'vue';
import { Dictionary } from '@src/application/dictionary.ts';

const stored: { [key: string]: Ref|object } = {};
const storedReactivity: { [key: string]: 'isRef'|'isReactive' } = {};
const createdEventListeners: Dictionary<boolean> = {};

function getLocalStorageValue(name: string) {
    const value = window.localStorage.getItem(name);
    if (value === null) {
        return null;
    }

    return JSON.parse(value);
}

function setLocalStorageValue(name: string, value: Ref<any>|object) {
    if (storedReactivity[name] === 'isRef') {
        if (window.localStorage.getItem(name) === JSON.stringify((value as Ref).value)) {
            return;
        }
        (value as Ref).value = getLocalStorageValue(name);
        return;
    }

    Object.assign(value, getLocalStorageValue(name));
}

export function asStore<valueType>(name: string, cb: () => valueType, localStorage: boolean = false): valueType {
    if (!stored[name]) {
        const value = cb();
        if (!isReactive(value) && !isRef(value)) {
            throw new Error('one can only store reactive values');
        }

        if (isRef(value)) {
            storedReactivity[name] = 'isRef';
        } else {
            storedReactivity[name] = 'isReactive';
        }

        if (localStorage && getLocalStorageValue(name)) {
            setLocalStorageValue(name, (value as unknown as object | Ref<any>));
        }

        stored[name] = (value as unknown as object | Ref<any>);
    }

    if (localStorage) {
        watch(() => stored[name], () => {
            if (window.localStorage.getItem(name) === JSON.stringify(unref(stored[name]))) {
                return;
            }
            window.localStorage.setItem(name, JSON.stringify(unref(stored[name])));
        }, { immediate: true, deep: true, flush: 'sync' });

        if (!createdEventListeners[name]) {
            window.addEventListener('storage', () => {
                setLocalStorageValue(name, stored[name]);
            });
            createdEventListeners[name] = true;
        }

    }

    return stored[name] as unknown as valueType;
}
